import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { GetEmployeeInformationInterface } from "@type/employees.types"
import { employeesInformationService } from "services"
import { AppThunk, RootState } from "store"

export interface EmployeeInformationStateInterface {
	list: GetEmployeeInformationInterface[]
	pageResults: number
	loading: boolean
	saving: boolean
}

const initialState: EmployeeInformationStateInterface = {
	list: [],
	pageResults: 0,
	loading: false,
	saving: false,
}

const informationSlice = createSlice({
	name: "information",
	initialState,
	reducers: {
		requestEmployeesInformation: employee => {
			employee.loading = true
		},
		receiveEmployeesInformation: (
			employee,
			action: PayloadAction<{
				employees: GetEmployeeInformationInterface[]
				offset: number
				pageResults: number
			}>,
		) => {
			const { employees, offset, pageResults } = action.payload
			if (offset === 0) {
				employee.list = employees
			} else {
				employee.list = [...employee.list, ...employees]
			}
			employee.pageResults = pageResults
			employee.loading = false
		},

		receiveEmployeesInformationFailed: employee => {
			employee.loading = false
		},
	},
})

//REDUCER
export default informationSlice.reducer

//ACTIONS
export const { requestEmployeesInformation, receiveEmployeesInformation, receiveEmployeesInformationFailed } =
	informationSlice.actions

const getEmployeesInformationByLocation =
	(
		locationId: string,
		dates: { startTime: string; finishTime: string }[],
		options: { limit: number; offset: number },
	): AppThunk =>
	async dispatch => {
		try {
			dispatch(requestEmployeesInformation())
			const { data: employeeResponse } = await employeesInformationService.getEmployeesInformationByLocation(
				locationId,
				dates,
				options,
			)
			dispatch(
				receiveEmployeesInformation({
					employees: employeeResponse.employees,
					offset: options.offset,
					pageResults: employeeResponse.pageResults,
				}),
			)
		} catch (error) {
			dispatch(receiveEmployeesInformationFailed())
		}
	}

export { getEmployeesInformationByLocation }

//SELECTORS
const selectEmployeeInformationState = (state: RootState) => state.employee.information
const isEmployeeInformationLoading = () => (state: RootState) => selectEmployeeInformationState(state).loading
const isEmployeeInformationSaving = () => (state: RootState) => selectEmployeeInformationState(state).saving
const selectEmployeeInformationList = () => (state: RootState) => selectEmployeeInformationState(state)?.list ?? []
const selectEmployeeInformationPageResults = () => (state: RootState) =>
	selectEmployeeInformationState(state).pageResults

export {
	selectEmployeeInformationList,
	selectEmployeeInformationState,
	isEmployeeInformationLoading,
	isEmployeeInformationSaving,
	selectEmployeeInformationPageResults,
}
